/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { ThemeProvider } from 'modules/common/contexts/theme-context';
import { Provider, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { ENVIRONMENT } from 'config';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import useTheme from 'modules/common/hooks/use-theme';
import createTheme from 'modules/common/theme';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Router from './routes';
import store from './store';
import { selectAppTheme } from './modules/dashboard/selectors';

/**
 * @returns Entry point in the react application. And also register the providers
 * store-provider :-The single Redux store in your application.
 * BrowserRouter :-  BrowserRouter is a router implementation that uses the HTML5 history API(pushState,
 * replaceState and the popstate event) to keep your UI in sync with the URL (https://reactrouter.com/en/main/router-components/browser-router)
 * ThemeProvider :- Common theme context for entire application
 * MuiThemeProvider :- It makes the theme available down the React tree thanks to React context (https://v3.mui.com/api/mui-theme-provider/)
 * Router :- Entire application protected and public routes
 * ToastContainer :- The ToastContainer is just a simple component, which you can write text or even custom (https://fkhadra.github.io/react-toastify/api/toast-container/)
 * JSX elements in to customize the toast even more
 */
const App = () => {
  const isPtg = window.location.origin === ENVIRONMENT.APP_URL;
  // change favicon and title based on domain
  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href = isPtg
        ? `${ENVIRONMENT.APP_URL}/${ENVIRONMENT.FAVICON}`
        : `${ENVIRONMENT.AIRO_APP_URL}/${ENVIRONMENT.AIRO_FAVICON}`;
    }
  }, []);
  //
  return (
    <Provider store={store}>
      <BrowserRouter>
        <ThemeProvider>
          <ThemeWrapper />
        </ThemeProvider>
      </BrowserRouter>
    </Provider>
  );
};
//
const ThemeWrapper = () => {
  const { theme } = useTheme();
  const appTheme = useSelector(selectAppTheme);
  //

  return (
    <MuiThemeProvider theme={createTheme(appTheme ?? theme)}>
      <Router />
      <ToastContainer />
    </MuiThemeProvider>
  );
};
//
export default App;
